import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../lib/api-client';

import type {
  SlaBreachRaw,
  SlaDashboardResultRaw,
  SlaDefinitionRaw,
  SlaHeatmapCellRaw,
  SlaRankingEntryRaw,
  SlaTemporalPatternRaw,
} from '../types';

export const SLA_KEY = ['sla'] as const;

export function useSlaDashboard(): {
  data: SlaDashboardResultRaw | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: [...SLA_KEY, 'dashboard'],
    queryFn: () => api.getSlaDashboard(),
    staleTime: 60 * 1000,
  });
  return { data: q.data, isLoading: q.isLoading, isError: q.isError };
}

export function useSlaDefinitions(): {
  data: { data: SlaDefinitionRaw[] } | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: [...SLA_KEY, 'definitions'],
    queryFn: () => api.listSlaDefinitions(),
    staleTime: 5 * 60 * 1000,
  });
  return { data: q.data, isLoading: q.isLoading, isError: q.isError };
}

export function useSlaHeatmap(slaDefinitionId: string | undefined): {
  data: { data: SlaHeatmapCellRaw[] } | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: [...SLA_KEY, 'heatmap', slaDefinitionId],
    enabled: slaDefinitionId !== undefined,
    queryFn: () => api.getSlaHeatmap(slaDefinitionId!),
    staleTime: 60 * 1000,
  });
  return { data: q.data, isLoading: q.isLoading, isError: q.isError };
}

export function useSlaRanking(periodDays: number): {
  data: { data: SlaRankingEntryRaw[] } | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: [...SLA_KEY, 'ranking', periodDays],
    queryFn: () => api.getSlaRanking(periodDays),
    staleTime: 60 * 1000,
  });
  return { data: q.data, isLoading: q.isLoading, isError: q.isError };
}

// T5-2 drill-down — violações reais paginadas por cursor (espelha useConflictAlertHistoryInfinite).
// breaches retorna { items, nextCursor } (sem wrapper `data`) → flatMap p.items.
export function useSlaBreaches(slaDefinitionId: string | undefined): {
  rows: SlaBreachRaw[];
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  isError: boolean;
} {
  const query = useInfiniteQuery({
    queryKey: [...SLA_KEY, 'breaches', slaDefinitionId],
    enabled: slaDefinitionId !== undefined,
    queryFn: ({ pageParam }) =>
      api.getSlaBreaches({
        ...(slaDefinitionId !== undefined ? { slaDefinitionId } : {}),
        ...(pageParam != null ? { cursor: pageParam } : {}),
        limit: 25,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  return {
    rows: (query.data?.pages ?? []).flatMap((p) => p.items),
    fetchNextPage: () => void query.fetchNextPage(),
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

// T5-2 padrões temporais detectados (Pro+) — só ativos por default.
export function useSlaPatterns(slaDefinitionId?: string): {
  data: { data: SlaTemporalPatternRaw[] } | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: [...SLA_KEY, 'patterns', slaDefinitionId],
    queryFn: () =>
      api.listSlaPatterns({
        ...(slaDefinitionId !== undefined ? { slaDefinitionId } : {}),
        status: 'active',
      }),
    staleTime: 60 * 1000,
  });
  return { data: q.data, isLoading: q.isLoading, isError: q.isError };
}

// T5-2 dispensar padrão — invalida a lista de padrões no sucesso.
export function useDismissPattern(): {
  mutate: (id: string) => void;
  isPending: boolean;
  isError: boolean;
} {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (id: string) => api.dismissSlaPattern(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...SLA_KEY, 'patterns'] });
    },
  });
  return { mutate: m.mutate, isPending: m.isPending, isError: m.isError };
}
